import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";

interface TruncatedMessageProps {
  content: string;
  maxHeight?: number; // in vh, default 50
}

export function TruncatedMessage({ content, maxHeight = 50 }: TruncatedMessageProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldTruncate, setShouldTruncate] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkHeight = () => {
      if (contentRef.current) {
        const maxHeightPx = (window.innerHeight * maxHeight) / 100;
        setShouldTruncate(contentRef.current.scrollHeight > maxHeightPx);
      }
    };

    checkHeight();
    window.addEventListener("resize", checkHeight);
    return () => window.removeEventListener("resize", checkHeight);
  }, [content, maxHeight]);

  const maxHeightStyle = isExpanded ? "none" : `${maxHeight}vh`;

  return (
    <div className="relative">
      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-300"
        style={{ maxHeight: shouldTruncate ? maxHeightStyle : "none" }}
      >
        <p className="text-sm whitespace-pre-wrap break-words">{content}</p>
      </div>

      {shouldTruncate && (
        <div className={`${!isExpanded ? "absolute bottom-0 left-0 right-0 pt-8 bg-gradient-to-t from-inherit" : ""}`}>
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-7 text-xs gap-1 mt-1 hover:bg-black/5 dark:hover:bg-white/5"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp size={14} />
                Voir moins
              </>
            ) : (
              <>
                <ChevronDown size={14} />
                Voir plus
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
